import { ToastrService } from 'ngx-toastr';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Meta, Title } from '@angular/platform-browser';
import { identity, isEqual, map, omit, pickBy } from 'lodash';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, catchError, of, switchMap, takeUntil } from 'rxjs';
import { Component, OnInit, ChangeDetectorRef, Renderer2, Inject, PLATFORM_ID } from '@angular/core';

import { StorageHelper } from '@diktup/frontend/helpers';
import { LandingPagesWithoutTranslationType, LandingPageTypeEnum, PointOfSaleType, RequestTypeEnum } from '@sifca-monorepo/ecommerce-generator';

import { LandingPagesService, SeoService, SharedService, AppService, PosService } from '../services';

@Component({
  template: '',
})
export class BaseContactComponent implements OnInit {
  public unsubscribeAll: Subject<any> = new Subject<any>();

  zoom = 8;
  seo: any;
  latitude: number;
  longitude: number;
  posId: string = '';
  markerPositions = [];
  pos: PointOfSaleType;
  contactForm: FormGroup;
  isButtonDisabled = false;
  center = { lat: 0, lng: 0 };
  pos$ = this.posService.pos$;
  landingPage: LandingPagesWithoutTranslationType[];
  defaultPicture = 'assets/images/contact-header-bg.jpg';

  constructor(
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected appService: AppService,
    protected seoService: SeoService,
    protected titleTagService: Title,
    protected posService: PosService,
    protected formBuilder: FormBuilder,
    protected translate: TranslateService,
    protected sharedService: SharedService,
    protected toastrService: ToastrService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected landingPagesService: LandingPagesService,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(PLATFORM_ID) protected platformId: Object,
    protected http: HttpClient,
    protected storageHelper: StorageHelper,
  ) {
    this.landingPagesService.landingPages$.pipe(takeUntil(this.unsubscribeAll)).subscribe((res) => {
      if (res?.objects?.length) {
        this.landingPage = res?.objects;
        this.changeDetectorRef.markForCheck();
      }
    });
    this.landingPagesService
      .findLandingPagesByTargetAndTypePaginated([LandingPageTypeEnum.CONTACT])
      .pipe(
        catchError(() => {
          return of(null);
        }),
        switchMap((response) => {
          this.landingPage = response.objects;
          return this.seoService.getPagesSEOJSON(this.landingPage[0]?.id);
        }),
      )
      .subscribe((res) => {
        this.seo = res;
        if (this.seo?.title) {
          this.titleTagService.setTitle(this.seo.title);
        }
        this.sharedService.tags$ = [
          ...this.metaTagService.addTags(
            map(this.seo?.metas, (meta) => pickBy(meta, identity)).filter(
              (meta: any) => `${meta?.name ?? ''}`.toLowerCase() !== 'viewport' && meta?.charset === undefined,
            ),
          ),
          { charset: 'UTF-8' },
        ];
        this.sharedService.links$ = map(this.seo?.links, (seoLink) => {
          const link = this.renderer.createElement('link');
          if (seoLink.sizes) {
            link.sizes = seoLink.sizes;
          }
          if (seoLink.href) {
            link.href = seoLink.href;
          }
          if (seoLink.rel) {
            link.rel = seoLink.rel;
          }
          this.renderer.appendChild(this.document.head, link);
          return link;
        });
        this.changeDetectorRef.markForCheck();
      });
  }

  ngOnInit(): void {
    this.posId = localStorage.getItem('posId');
    this.posService.getPos(this.posId).subscribe((response) => {
      this.pos = response;
      for (const location of this.pos.locations) {
        const latitude = location.location.coordinates[1];
        const longitude = location.location.coordinates[0];
        this.markerPositions.push({ lat: latitude, lng: longitude });
      }
      if (this.markerPositions.length > 0) {
        const sumLat = this.markerPositions.reduce((acc, pos) => acc + pos.lat, 0);
        const sumLng = this.markerPositions.reduce((acc, pos) => acc + pos.lng, 0);

        const avgLat = sumLat / this.markerPositions.length;
        const avgLng = sumLng / this.markerPositions.length;

        this.center = { lat: avgLat, lng: avgLng };
      }
      this.changeDetectorRef.markForCheck();
    });
    this.contactForm = this.formBuilder.group({
      requestor: this.formBuilder.group({
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        phone: this.formBuilder.group({
          countryCode: [''],
          number: [''],
        }),
      }),
      request: this.formBuilder.group({
        subject: ['', Validators.required],
        content: ['', Validators.required],
      }),
      type: [RequestTypeEnum.CONTACT],
    });
    const initialValues = this.contactForm.value;
    this.contactForm.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((ivalues) => {
      this.isButtonDisabled = isEqual(ivalues, initialValues);
    });
  }

  save() {
    this.isButtonDisabled = true;
    const requestor = {
      ...omit(this.contactForm.get('requestor').value, 'phone'),
      ...(this.contactForm.get(['requestor', 'phone', 'number']).value ? { phone: this.contactForm.get(['requestor', 'phone']).value } : {}),
    };
    const input: any = {
      requestor,
      ...omit(this.contactForm.value, 'requestor'),
      type: RequestTypeEnum.CONTACT,
    };
    this.appService
      .createRequest(this.posId, input)
      .pipe(
        catchError(() => {
          this.translate.get('SHARED.SOMETHING_WENT_WRONG').subscribe((text: string) => {
            this.toastrService.error(text);
          });
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.contactForm.reset();
          this.translate.get('SHARED.CONTACT_MESSAGE_RECEIVED').subscribe((text: string) => {
            this.toastrService.success(text);
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  ngOnDestroy() {
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
